import { baseRent, furnFx } from "./studio";
import { C, type Factory } from "@bd/content";
import { BALANCE } from "./balance";
import { priceIndex, WEEKS_PER_YEAR } from "./calendar";
import { baseUnitCost, copiesPerPallet } from "./design";
import type { Company, Design, GameState, Loan, PrintOrder, Product } from "./state";
import { hasRoom, hasTrait, palletCapacity, roomFx, traitFx } from "./studio";
import { clamp, money, newId, rng } from "./util";
import { perkFx } from "./perks";
import { modValue } from "./mods";
import { dpow } from "./dmath";

// ------------------------------------------------------------------ quotes

export interface PrintQuote {
  factory: string;
  copies: number;
  unitCost: number;
  setup: number;
  total: number;
  leadWeeks: number;
  minOrder: number;
  defectChance: number;
  quality: number;
  why: string[];
}

export function productionDiscount(co: Company): number {
  let d = 0;
  for (const s of co.staff) {
    if (s.role === "production") d += 0.04 + s.stats.craft / 1500;
    d -= traitFx(s, "unitCost");
  }
  d -= perkFx(co, "unitCost");
  return clamp(d, 0, 0.3);
}

export function defectChance(co: Company, f: Factory): number {
  let p = f.defect;
  for (const s of co.staff) {
    if (s.role === "production") p *= 0.75;
    p *= 1 + traitFx(s, "defect");
  }
  p *= 1 + perkFx(co, "defect");
  return clamp(p, 0.003, 0.4);
}

export function factoryAvailable(co: Company, f: Factory): boolean {
  if (f.needsRoom) return hasRoom(co, f.needsRoom);
  return co.unlocked.includes(`fa:${f.id}`);
}

export function quotePrint(state: GameState, co: Company, d: Design, factoryId: string, copies: number): PrintQuote {
  const f = C.factory[factoryId];
  const why: string[] = [];
  const idx = priceIndex(state.week);
  let unit = baseUnitCost(d) * f.costMult * idx;
  why.push(`Parts: ${money(baseUnitCost(d) * idx)} a copy`);
  why.push(f.costMult >= 1 ? `${f.name} adds ${Math.round((f.costMult - 1) * 100)}%` : `${f.name} is ${Math.round((1 - f.costMult) * 100)}% cheaper`);
  const disc = productionDiscount(co);
  if (disc > 0) {
    unit *= 1 - disc;
    why.push(`Your production team saves ${Math.round(disc * 100)}%`);
  }
  if (state.market.shortage) {
    unit *= state.market.shortage.costMult;
    why.push(`${state.market.shortage.kind === "paper" ? "Card-stock shortage" : "Import tariff"}: +${Math.round((state.market.shortage.costMult - 1) * 100)}%`);
  }
  const eventCosts = modValue(co, "costs");
  if (eventCosts !== 1) {
    unit *= eventCosts;
    why.push(eventCosts > 1 ? `Higher costs this season: +${Math.round((eventCosts - 1) * 100)}%` : `Lower costs this season: ${Math.round((1 - eventCosts) * 100)}% off`);
  }
  // volume discount
  const vol = copies >= 10000 ? 0.85 : copies >= 5000 ? 0.9 : copies >= 2500 ? 0.95 : 1;
  if (vol < 1) {
    unit *= vol;
    why.push(`Bulk discount: ${Math.round((1 - vol) * 100)}%`);
  }
  let quality = f.quality;
  // your own press: its machines lift the quality and the conveyor gets boxes out faster
  if (f.id === "own_shop") quality += roomFx(co, "printQuality") + furnFx(co, "printQuality") + (f.upgradeQuality ?? 0) * Math.max(0, co.rooms.filter((r) => r.type === "print_shop").length - 1);
  const lead = f.id === "own_shop" ? Math.max(1, Math.round(f.leadWeeks * (1 - furnFx(co, "printSpeed")))) : f.leadWeeks;
  quality += modValue(co, "printQuality");
  const setup = Math.round(f.setup * idx);
  const n = Math.max(copies, 0);
  return {
    factory: f.id, copies: n, unitCost: Math.round(unit * 100) / 100, setup, total: Math.round(unit * n + setup), leadWeeks: lead,
    minOrder: f.minOrder, defectChance: defectChance(co, f), quality: clamp(quality), why,
  };
}

export function placeOrder(state: GameState, co: Company, p: Product, q: PrintQuote): PrintOrder {
  const r = rng(state, "production");
  const order: PrintOrder = { id: newId(state, "o"), factory: q.factory, copies: q.copies, unitCost: q.unitCost, arriveWeek: state.week + q.leadWeeks, quality: q.quality };
  if (r.chance(q.defectChance)) order.misprint = r.pick(["cards", "pieces"] as const);
  // "cousin:<id>": a cousin's own print shop (see cousins.ts)
  const f = C.factory[q.factory] ?? C.factory.own_shop;
  if (f.shipping && r.chance(0.12)) {
    order.misprint = order.misprint ?? "port";
    order.arriveWeek += r.int(2, 6);
  }
  p.orders.push(order);
  co.cash -= q.total;
  co.deductible = (co.deductible ?? 0) + q.total;
  return order;
}

// ------------------------------------------------------------------ warehouse

export function palletsUsed(co: Company): number {
  let n = 0;
  for (const p of co.products) if (p.stock > 0) n += Math.ceil(p.stock / copiesPerPallet(p.design));
  return n;
}

/** Weekly cost of renting outside storage for pallets over capacity. */
export function storageCost(state: GameState, co: Company): { pallets: number; capacity: number; over: number; cost: number } {
  const pallets = palletsUsed(co);
  const capacity = palletCapacity(co);
  const over = Math.max(0, pallets - capacity);
  // forklifts, trolleys and tape stations make storage cheaper to run
  return { pallets, capacity, over, cost: Math.round(over * BALANCE.palletWeek * priceIndex(state.week) * (1 - furnFx(co, "logistics"))) };
}

// ------------------------------------------------------------------ big bets

/**
 * Why you can't buy a rival right now, or null: one buyout every five years while you fold the last one in,
 * and the regulators stop you at four. Keeps the market a market.
 */
export function buyoutBlocked(state: GameState, co: Company): string | null {
  const mine = state.rivals.filter((r) => r.acquiredBy === co.id);
  if (mine.length >= BALANCE.maxBuyouts) return `Regulators won't let you buy a ${mine.length + 1}th publisher: you already own ${mine.length}.`;
  const last = Math.max(-1e9, ...mine.map((r) => r.acquiredWeek ?? r.bustWeek ?? -1e9));
  const wait = last + BALANCE.buyoutGapYears * WEEKS_PER_YEAR - state.week;
  if (wait > 0) return `You're still folding in your last buyout: the next can come in ${Math.ceil(wait / WEEKS_PER_YEAR)} year${Math.ceil(wait / WEEKS_PER_YEAR) === 1 ? "" : "s"}.`;
  return null;
}

/** A marketing campaign's price: its base cost, scaled up for a publisher with bigger sales (a bigger campaign). */
export function campaignCost(co: Company, base: number, week: number): number {
  const idx = priceIndex(week);
  const yearly = co.ledger.reduce((s, w) => s + w.income, 0) * (WEEKS_PER_YEAR / Math.max(1, co.ledger.length));
  return Math.round(base * idx * Math.max(1, dpow(yearly / (60000 * idx), 0.35)));
}

/** Tax on the year's profit above a threshold, paid each January. */
export function taxDue(co: Company, week: number): number {
  const idx = priceIndex(week);
  // print runs and campaigns are business costs too
  const profit = co.ledger.reduce((s, w) => s + w.income - w.costs, 0) - (co.deductible ?? 0);
  const band = BALANCE.taxHigh * idx;
  const low = Math.max(0, Math.min(profit, band) - BALANCE.taxFree * idx);
  const high = Math.max(0, profit - band);
  return Math.round(low * BALANCE.taxRate + high * BALANCE.taxHighRate);
}

/** What buying out a rival publisher costs: their cash, their catalogue and their name, with a premium. */
export function rivalPrice(state: GameState, rivalId: string): number {
  const rv = state.rivals.find((r) => r.id === rivalId);
  if (!rv) return Infinity;
  const idx = priceIndex(state.week);
  return Math.round((Math.max(0, rv.cash) + rv.released * 12000 * idx + rv.reputation * 25000 * idx) * 1.4);
}

// ------------------------------------------------------------------ loans

export function creditLimit(state: GameState, co: Company): number {
  const yearly = co.ledger.reduce((s, w) => s + w.income, 0) * (WEEKS_PER_YEAR / Math.max(1, co.ledger.length));
  const owed = co.loans.filter((l) => l.lender !== "gran").reduce((s, l) => s + l.principal, 0); // the bank ignores family IOUs
  const base = (6000 * priceIndex(state.week) + yearly * 0.3 + co.reputation * 60 * priceIndex(state.week)) * (1 + perkFx(co, "credit")) + (co.creditBonus ?? 0);
  // the bank is kinder to the company in last place (gentle catch-up)
  const kinder = state.companies.length > 1 && isLastPlace(state, co) ? 1.3 : 1;
  return Math.max(0, Math.round(base * kinder - owed));
}

/** What a company owns besides cash: its buildings, shops in other towns, rooms, and royalty deals still paying out. */
export function assetValue(co: Company, week: number): number {
  const idx = priceIndex(week);
  const order = ["garage", "office", "studio", "hq"];
  let v = 0;
  // buildings: half of what moving in would cost today
  // at what it would sell for: buildings hold over half their value, shops and fit-outs much less
  for (const lv of order.slice(1, order.indexOf(co.level) + 1)) v += C.level[lv].moveCost * idx * 0.55;
  for (const a of co.annexes ?? []) v += (C.annex[a.kind]?.cost ?? 0) * idx * 0.55;
  for (const id of co.stores ?? []) v += (C.store[id]?.cost ?? 0) * idx * 0.3;
  for (const r of co.rooms) v += (C.room[r.type]?.cost ?? 0) * idx * 0.2;
  for (const m of co.mods) if (m.kind === "royalty") v += m.value * idx * Math.max(0, m.until - week) * 0.5;
  return v;
}

export function netWorth(co: Company, week?: number): number {
  const stock = co.products.reduce((s, p) => s + p.stock * p.unitCost * 0.5, 0);
  const owed = co.loans.reduce((s, l) => s + l.principal, 0);
  return co.cash + stock - owed + (week === undefined ? 0 : assetValue(co, week));
}

export function isLastPlace(state: GameState, co: Company): boolean {
  const worths = state.companies.map((c) => netWorth(c, state.week));
  return netWorth(co, state.week) <= Math.min(...worths);
}

export function makeLoan(state: GameState, lender: Loan["lender"], amount: number, weeks: number, yearlyRate: number): Loan {
  const rate = yearlyRate / WEEKS_PER_YEAR;
  const payment = rate > 0 ? (amount * rate) / (1 - dpow(1 + rate, -weeks)) : amount / weeks;
  return { id: newId(state, "l"), lender, principal: amount, rate, weeksLeft: weeks, payment: Math.round(payment * 100) / 100 };
}

export function loanWeek(l: Loan): { paid: number; interest: number } {
  const interest = l.principal * l.rate;
  const paid = Math.min(l.payment, l.principal + interest);
  l.principal = Math.max(0, l.principal + interest - paid);
  l.weeksLeft -= 1;
  return { paid, interest };
}

// ------------------------------------------------------------------ cash flow

export interface CashFlowWeek {
  week: number;
  in: number;
  out: number;
  cash: number;
}

/**
 * Projects cash over the coming weeks: money out at print time and for payroll, money in from expected sales.
 * The UI draws this so the player can always see the gap they need to survive.
 */
export function cashForecast(state: GameState, co: Company, weeks: number, extraOut: { week: number; amount: number }[] = []): CashFlowWeek[] {
  const out: CashFlowWeek[] = [];
  let cash = co.cash;
  const recent = co.ledger.slice(-8);
  const avgIncome = recent.length ? recent.reduce((s, w) => s + w.income, 0) / recent.length : 0;
  const weeklyCosts = co.staff.reduce((s, x) => s + x.salary, 0) + baseRent(co) * priceIndex(state.week) + co.loans.reduce((s, l) => s + l.payment, 0);
  for (let i = 1; i <= weeks; i++) {
    const w = state.week + i;
    const decay = dpow(0.97, i);
    const income = avgIncome * decay;
    let spend = weeklyCosts;
    for (const e of extraOut) if (e.week === w) spend += e.amount;
    cash += income - spend;
    out.push({ week: w, in: Math.round(income), out: Math.round(spend), cash: Math.round(cash) });
  }
  return out;
}

// ------------------------------------------------------------------ forecasts

export interface Forecast {
  low: number;
  mid: number;
  high: number;
  confidence: number; // 0..1, narrower with marketers and past data
}

/** A range with a confidence band, never one number. Marketing staff and past sales narrow it. */
export function forecastRange(expected: number, co: Company): Forecast {
  const marketers = co.staff.filter((s) => s.role === "marketer");
  let spread = 0.65;
  for (const m of marketers) spread *= 1 - 0.12 - m.stats.charisma / 800;
  spread *= 1 - Math.min(0.35, co.stats.released * 0.02);
  spread *= 1 - roomFx(co, "forecast") - perkFx(co, "forecast");
  if (co.staff.some((s) => hasTrait(s, "trendspotter"))) spread *= 0.9;
  spread = clamp(spread, 0.12, 0.7);
  return { low: Math.round(expected * (1 - spread)), mid: Math.round(expected), high: Math.round(expected * (1 + spread * 1.4)), confidence: clamp(1 - spread) };
}
